import React, { useEffect, useMemo, useState } from "react";
import {
  Link,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import { FieldErrors, useForm, UseFormRegister } from "react-hook-form";
import { joiResolver } from "@hookform/resolvers/joi";

import useAuth from "../../hooks/useAuth";
import { privacyPolicyLocale } from "../../components/common/consentManager/privacyPolicyLocale";
import { toast } from "react-toastify";
import { Helmet } from "react-helmet-async";
import useAppLocalStorage from "../../hooks/useAppLocalStorage";
import { useTranslation } from "react-i18next";
import { getLanguageCode } from "../../utils/languageUtility";
import { useCookies } from "react-cookie";
import { ModalShell } from "../../components/atoms/ModalShell";
import { HiEye, HiEyeOff } from "react-icons/hi";
import { BiCheck, BiX } from "react-icons/bi";
import PasswordValidator from "../../components/public/PasswordValidator";
import SocialAuth from "../../components/pages/lmcUserVIew/SocialAuth";
import { IResolveParams } from "reactjs-social-login";
import { queryClient } from "../../providers/QueryProvider";
import CountryOptions from "../../components/public/CountryOptions";
import { CustomButton } from "../../components/atoms/CustomButton";
import { useCheckPassword } from "../../hooks/auth";
import {
  useCheckOwnerSocialAuth,
  useOwnderLogin,
  useOwnderLoginSocial,
  useOwnderRegister,
  useOwnderSendValidationLink,
} from "../../hooks/entity";
import { useGetVerifyPlansMutation } from "../../hooks/billing";
import SecureStorage from "../../utils/secureStorage";
import { extractUtmParamsFromSearch } from "../../components/pages/customRegister/utmCapture";
import {
  captureAttributionFromSearch,
  readPartnerLinkFromSearch,
} from "../../utils/attributionCapture";
import { readUtmDatasCookie } from "../../utils/utmDatasCookie";
import { Timer } from "../../utils/Timer";
import { shownFieldError } from "../../utils/validationPatterns";
import { registerSchema } from "./registerSchema";

type RegisterForm = {
  entity_name: string;
  entity_description: string;
  user_first_name: string;
  user_last_name: string;
  user_email: string;
  user_password: string;
  user_country_code: string;
  user_agree_terms: boolean;
};

interface Step1Props {
  errors: FieldErrors<RegisterForm>;
  apiErrors: Record<string, string>;
  register: UseFormRegister<RegisterForm>;
}

const Step1 = ({ errors, apiErrors, register }: Step1Props) => {
  const { t, i18n } = useTranslation();

  const [showPassword, setShowPassword] = useState(false);
  const [validatorCurrentPassword, setValidatorCurrentPassword] =
    useState<string>("");

  const rules = [
    {
      regex: "^.{8,32}$",
      message: t("ruleValidator.key1", { min: 8, max: 32 }),
    },
  ];

  return (
    <React.Fragment>
      <div
        className={`self-stretch bg-white rounded-md border ${
          Object.keys(errors).length > 0 ? "border-red-500" : "border-gray-300"
        } flex-col justify-start items-start flex`}
      >
        <div className="self-stretch px-3 py-2 bg-white rounded-tl-md rounded-tr-md shadow justify-start items-center inline-flex">
          <input
            type="email"
            placeholder={t("login.label_1").toString()}
            className="text-gray-700 focus:ring-0 p-0 border-none text-base font-normal font-figtree leading-normal w-full"
            {...register("user_email")}
          />
        </div>

        <div className="self-stretch px-3 py-2 bg-white rounded-bl-md rounded-br-md shadow border-t border-gray-300 justify-between items-center inline-flex">
          <input
            type={showPassword ? "text" : "password"}
            className="text-gray-700 focus:ring-0 p-0 border-none text-base font-normal font-figtree leading-normal w-full"
            placeholder={t("login.label_2").toString()}
            {...register("user_password", {
              onChange(event) {
                setValidatorCurrentPassword(event.target.value);
              },
            })}
          />

          <button
            className="w-5 h-5 relative"
            type="button"
            onClick={() => setShowPassword(!showPassword)}
          >
            {!showPassword ? (
              <HiEye className="text-gray-400" size={20} />
            ) : (
              <HiEyeOff className="text-gray-400" size={20} />
            )}
          </button>
        </div>

        <PasswordValidator
          className={validatorCurrentPassword.length > 0 ? "p-3" : "hidden"}
          ruleSet={rules}
          passwordValue={validatorCurrentPassword}
          passedRuleClassName="flex items-center text-green-500"
          failedRuleClassName="flex items-center text-red-500"
          passIcon={<BiCheck size={20} />}
          failIcon={<BiX size={20} />}
        />
      </div>

      <div className="self-stretch justify-between items-center inline-flex">
        <div className="justify-start items-center gap-2 flex">
          <input
            type="checkbox"
            className="w-4 h-4 relative bg-white rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            id="user_agree_terms"
            {...register("user_agree_terms")}
          />
          <label
            className={`${
              !!errors.user_agree_terms || apiErrors.user_agree_terms
                ? "text-red-600"
                : "text-gray-700"
            } text-sm font-normal font-figtree leading-tight`}
            htmlFor="user_agree_terms"
          >
            {t("register.step1.text_1") as string}{" "}
            <a
              href={`${process.env.REACT_APP_LANDING_URL}/${privacyPolicyLocale(
                i18n.language
              )}/privacy-policy`}
              target="_blank"
              className="text-right text-blue-600 text-sm font-medium font-figtree leading-tight"
              rel="noreferrer"
            >
              {t("register.step1.text_2")}
            </a>
          </label>
        </div>
      </div>
    </React.Fragment>
  );
};

interface Step2Props {
  errors: FieldErrors<RegisterForm>;
  register: UseFormRegister<RegisterForm>;
}

const Step2 = ({ errors, register }: Step2Props) => {
  const { t } = useTranslation();

  return (
    <div
      className={`w-full rounded-md border ${
        Object.keys(errors).length > 0 ? "border-red-500" : "border-gray-300"
      } flex-col justify-start items-start inline-flex`}
    >
      <div className="self-stretch justify-start items-start inline-flex">
        <div className="self-stretch px-3 py-2 bg-white rounded-tl-md shadow justify-start items-center inline-flex w-full">
          <input
            type="text"
            placeholder={t("register.step2.label_1").toString()}
            className="text-gray-700 focus:ring-0 p-0 border-none text-base font-normal font-figtree leading-normal w-full"
            {...register("user_first_name")}
          />
        </div>
        <div className="self-stretch px-3 py-2 bg-white rounded-tr-md shadow justify-start items-center inline-flex w-full">
          <input
            type="text"
            placeholder={t("register.step2.label_2").toString()}
            className="text-gray-700 focus:ring-0 p-0 border-none text-base font-normal font-figtree leading-normal w-full"
            {...register("user_last_name")}
          />
        </div>
      </div>
      <div className="self-stretch px-3 py-2 bg-white shadow border-t border-gray-300 justify-between items-center inline-flex">
        <select
          translate="no"
          className="w-full border-0 focus:ring-0 p-0 text-gray-700 text-base font-normal font-figtree leading-normal"
          {...register("user_country_code")}
        >
          <option value="">{t("register.step2.label_3")}</option>
          <CountryOptions />
        </select>
      </div>
      <div className="self-stretch px-3 py-2 bg-white shadow border-t border-gray-300  justify-start items-center inline-flex">
        <input
          type="text"
          placeholder={t("register.step2.label_4").toString()}
          className="text-gray-700 focus:ring-0 p-0 border-none text-base font-normal font-figtree leading-normal w-full"
          {...register("entity_name")}
        />
      </div>
      <div className="self-stretch px-3 py-2 bg-white border-t border-gray-300  rounded-bl-md rounded-br-md shadow justify-start items-center inline-flex">
        <input
          type="text"
          placeholder={t("register.step2.label_5").toString()}
          className="text-gray-700 focus:ring-0 p-0 border-none text-base font-normal font-figtree leading-normal w-full"
          {...register("entity_description")}
        />
      </div>
    </div>
  );
};

const Register = () => {
  const localStorage = useAppLocalStorage();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation() as { state?: { from?: { pathname: string } } };
  const [searchParams] = useSearchParams();
  const auth = useAuth();
  const [cookies] = useCookies(["utm_datas", "_fbp", "_fbc"]);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setError,
    setValue,
    trigger,
    getValues,
  } = useForm<RegisterForm>({
    resolver: joiResolver(registerSchema),
    defaultValues: {
      entity_name: "",
      entity_description: "",
      user_first_name: "",
      user_last_name: "",
      user_email: "",
      user_password: "",
      user_country_code: "",
      user_agree_terms: false,
    },
  });

  const from = location.state?.from?.pathname || "/control-panel";

  useEffect(() => {
    let cleaner = true;

    if (cleaner && auth.user.isAuthenticated) {
      if (searchParams.has("language"))
        navigate(from + `?language=${searchParams.get("language")}`);
      else navigate(from);
    }

    let planInPath = searchParams.get("plan");

    if (cleaner && planInPath) SecureStorage.setSelectedPlan(planInPath);

    if (cleaner) {
      captureAttributionFromSearch(searchParams.toString());
    }
    reset();

    return () => {
      cleaner = false;
    };
  }, [auth, from, navigate, searchParams, reset, localStorage]);

  const [step, setStep] = useState<1 | 2>(1);
  const [showTimer, setShowTimer] = useState(false);
  const [showConfirmEmailModal, setShowConfirmEmailModal] = useState(false);
  interface UserData {
    owner_user_identification: string;
    entity_identification: string;
    user_email: string;
    user_password: string;
    is_new_user: boolean;
  }

  const [userData, setUserData] = useState<UserData | null>(null);
  const [apiErrors, setApiErrors] = useState<Record<string, string>>({});
  const [isSocialAuthInProgress, setIsSocialAuthInProgress] = useState(false);
  const [socialAuthPayload, setSocialAuthPayload] = useState<{
    access_token: string;
    provider: string;
  }>({
    access_token: "",
    provider: "",
  });
  const { mutate: checkPassword, isLoading: isChecking } = useCheckPassword();
  const {
    mutate: registerOwner,
    isLoading: isRegistering,
    error,
  } = useOwnderRegister();

  const { mutate: resendValitionLink, isLoading: isResending } =
    useOwnderSendValidationLink();

  const { mutate: ownerLogin, isLoading: isLogin } = useOwnderLogin();

  const { mutate: ownerLoginSocial, isLoading: isSocialLogin } =
    useOwnderLoginSocial();

  const { mutate: checkOwnerSocialAuth, isLoading: isCheckingSocialAuth } =
    useCheckOwnerSocialAuth();

  const { mutateAsync: getVerifiedPlans } = useGetVerifyPlansMutation();

  const handleLogin = () => {
    if (!userData) return;

    const data = {
      user_email: userData.user_email,
      user_password: userData.user_password,
    };

    ownerLogin(
      { credentials: data },
      {
        onSuccess: async (res) => {
          queryClient.invalidateQueries("payment-status");
          await handleVerifyPlansResult(res.data.element, false);
        },
      }
    );
  };

  const handleRegisterViaSocial = async () => {
    if (!isSocialAuthInProgress) return;

    const result = await trigger([
      "user_first_name",
      "user_last_name",
      "user_country_code",
      "entity_name",
      "entity_description",
    ]);
    if (!result) return;

    const { user_email, user_password, user_agree_terms, ...rest } =
      getValues();
    const newData = { ...rest, user_utm_data: getUserUtmData() };

    const dataPayload = {
      type: "register",
      providerPayload: socialAuthPayload,
      ...newData,
    };

    ownerLoginSocial(
      { data: dataPayload },
      {
        onSuccess: async ({ data }) => {
          SecureStorage.setOAuthCredentials(socialAuthPayload);
          queryClient.invalidateQueries("payment-status");

          setUserData({
            owner_user_identification:
              data.element.owner_user_identification ??
              data.element.user_identification,
            entity_identification: data.element.entity_identification,
            user_email: data.element.user_email,
            user_password: "",
            is_new_user: true,
          });

          await handleVerifyPlansResult(data.element, true, true);
        },
        onError: (error) => {
          handleValidationError(error);
          SecureStorage.clearOAuthCredentials();
        },
      }
    );
  };

  const goToEntityDetailsStep = (
    cpPayload: { access_token: string; provider: string },
    profile?: { user_first_name?: string; user_last_name?: string }
  ) => {
    if (profile?.user_first_name)
      setValue("user_first_name", profile.user_first_name);
    if (profile?.user_last_name)
      setValue("user_last_name", profile.user_last_name);
    setIsSocialAuthInProgress(true);
    setSocialAuthPayload(cpPayload);
    setStep(2);
  };

  const handleResolveSocialAuth = (
    provider: IResolveParams["provider"],
    data: IResolveParams["data"]
  ) => {
    const cpPayload = {
      access_token:
        provider === "google"
          ? data?.access_token
          : data?.authorization?.id_token,
      provider,
    };

    checkOwnerSocialAuth(
      { data: { providerPayload: cpPayload } },
      {
        onSuccess: ({ data: checkRes }) => {
          SecureStorage.setOAuthCredentials(cpPayload);
          goToEntityDetailsStep(cpPayload, checkRes.element);
        },
        onError: (error) => {
          handleValidationError(error);
        },
      }
    );
  };

  const onSubmit = (data: any) => {
    const newData = {
      ...data,
      user_utm_data: getUserUtmData(),
    };

    registerOwner(
      { data: newData },
      {
        onSuccess: (res) => {
          setShowConfirmEmailModal(true);
          setUserData({
            ...res.data.element,
            user_email: data.user_email,
            user_password: data.user_password,
            is_new_user: true,
          });

          SecureStorage.setUserPreferences({
            user_email: data.user_email,
            rememberMe: data.user_agree_terms,
            is_new_user: true,
          });
        },
      }
    );
  };

  const readGaClientId = (): string | undefined => {
    try {
      const match = document.cookie.match(
        /(?:^|;\s*)_ga=GA\d+\.\d+\.(\d+\.\d+)/
      );
      return match ? match[1] : undefined;
    } catch {
      return undefined;
    }
  };

  const getUserUtmData = () => {
    if (!window.navigator.cookieEnabled) return undefined;

    let user_utm_data: Record<string, any> = readUtmDatasCookie(
      cookies.utm_datas
    );

    if (cookies.utm_datas?.fbclid) {
      if (cookies._fbp) user_utm_data.fbp = cookies._fbp;
      if (cookies._fbc) user_utm_data.fbc = cookies._fbc;
    }

    const stored = SecureStorage.getAttributionData();

    // Never searchParams.get('bp_id'): a relayed link past its 90 days must not be credited.
    const bp_id =
      readPartnerLinkFromSearch(searchParams)?.bp_id || stored?.bp_id;
    if (bp_id) user_utm_data.bp_id = bp_id;

    const gclid = searchParams.get("gclid") || stored?.gclid;
    if (gclid) user_utm_data.gclid = gclid;

    const wbraid = searchParams.get("wbraid") || stored?.wbraid;
    if (wbraid) user_utm_data.wbraid = wbraid;
    const gbraid = searchParams.get("gbraid") || stored?.gbraid;
    if (gbraid) user_utm_data.gbraid = gbraid;

    const gadSource = searchParams.get("gad_source") || stored?.gad_source;
    if (gadSource) user_utm_data.gad_source = gadSource;

    const utmFromStored = stored?.utm || {};
    for (const [key, value] of Object.entries(utmFromStored)) {
      if (value) user_utm_data[key] = value;
    }
    const utmFromUrl = extractUtmParamsFromSearch(searchParams.toString());
    for (const [key, value] of Object.entries(utmFromUrl)) {
      if (value) user_utm_data[key] = value;
    }

    const gaClientId = readGaClientId();
    if (gaClientId) user_utm_data.ga_client_id = gaClientId;

    return Object.keys(user_utm_data).length > 0 ? user_utm_data : undefined;
  };
  const handleVerifyPlansResult = async (
    element: any,
    rememberMe: boolean,
    isNewUser = false
  ) => {
    const userId =
      element?.owner_user_identification ?? element?.user_identification;
    const entityId = element?.entity_identification;
    if (!userId || !entityId) {
      console.error(
        "[handleVerifyPlansResult] register response missing required identifiers",
        { hasUserId: !!userId, hasEntityId: !!entityId }
      );
      toast.error("Registration response was incomplete. Please try again.");
      return;
    }
    await getVerifiedPlans(
      {
        user_identification: userId,
        entity_identification: entityId,
        access_token: element?.access_token,
      },
      {
        onSuccess: (value) => {
          const hasPlans = value?.data?.element?.customer_plans?.length;
          auth.onOffUserEndedSubscription(!hasPlans);

          auth.login({
            ...element,
            ...value.data.element,
            user_identification:
              element.owner_user_identification ?? element.user_identification,
            trial_left_days: value.data.element.trial_left_days,
            rememberMe,
          });

          if (isNewUser) SecureStorage.clearOAuthCredentials();

          SecureStorage.clearAttributionData();

          const chosenPlan = SecureStorage.getSelectedPlan();
          navigate(
            chosenPlan
              ? `${from}?plan_name=${encodeURIComponent(chosenPlan)}`
              : from,
            { replace: true }
          );
        },
        onError: (err) => {
          if (err.response?.data?.message !== "Payment checking")
            toast.error(err.response?.data?.message);
          else if (!err.response) toast.error(err.message);
        },
      }
    );
  };
  const handleValidationError = (error: any) => {
    toast.error(error.response?.data.message || error.message);
    if (error.response?.status === 422) {
      Object.entries(error.response?.data?.element).forEach(([key, value]) => {
        setError(
          key as
            | "entity_name"
            | "entity_description"
            | "user_first_name"
            | "user_last_name"
            | "user_email"
            | "user_password"
            | "user_country_code"
            | "user_agree_terms"
            | "root"
            | `root.${string}`,
          { type: "custom", message: value as string }
        );
      });
    }
  };

  const partnerReference = useMemo(() => {
    const bpId = getUserUtmData()?.bp_id;
    return typeof bpId === "string" ? bpId.trim().replace(/_idBP$/i, "") : "";
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, cookies.utm_datas]);

  const renderStep = () => {
    switch (step) {
      case 1:
        return (
          <Step1 errors={errors} apiErrors={apiErrors} register={register} />
        );
      case 2:
        return <Step2 errors={errors} register={register} />;
    }
  };

  const handleVerifyAndGoToStep2 = async () => {
    try {
      const result = await trigger([
        "user_email",
        "user_password",
        "user_agree_terms",
      ]);

      if (result) {
        const level = "level2";
        checkPassword(
          {
            password: getValues("user_password"),
            level,
          },
          {
            onSuccess: () => {
              setApiErrors({});
              setStep(2);
            },
          }
        );
      }
    } catch (error) {}
  };

  const renderStepButton = () => {
    switch (step) {
      case 1:
      default:
        return (
          <CustomButton
            variant="primary"
            className="w-full"
            onClick={handleVerifyAndGoToStep2}
            isLoading={isChecking}
            disabled={isChecking}
            title={t("register.renderStepNextButton.btn_1") as string}
          />
        );
      case 2:
        return (
          <CustomButton
            variant="primary"
            className="w-full"
            isLoading={
              isRegistering ||
              isSocialLogin ||
              queryClient.getQueryState(["payment-status"])?.isFetching
            }
            disabled={
              isRegistering ||
              isSocialLogin ||
              queryClient.getQueryState(["payment-status"])?.isFetching
            }
            onClick={
              isSocialAuthInProgress
                ? handleRegisterViaSocial
                : handleSubmit(onSubmit)
            }
            title={t("register.registerBtn") as string}
          />
        );
    }
  };

  const handleRejectSocialAuth = (err: any) => {
    console.log(err);
  };

  return (
    <React.Fragment>
      {step === 2 && (
        <CustomButton
          icon="arrowLeft"
          variant="none"
          className="text-gray-500 text-decoration-none px-0 mb-1 absolute top-24 left-5 flex items-center gap-2"
          onClick={() => {
            if (isSocialAuthInProgress) reset();
            setStep(1);
          }}
          title={t("common.back") as string}
        />
      )}

      <div className="auth-page min-vh-100 d-flex align-items-center">
        <Helmet>
          <title>Sign up | Super User Manager</title>
          <meta
            name="description"
            content="Register to the easiest and most secure way to manage your users."
          />
          <link
            rel="canonical"
            href={`${process.env.REACT_APP_HOST_URL}/register`}
          />
        </Helmet>

        <div className="md:w-[448px] w-full flex-col justify-start items-start inline-flex">
          <div className="self-stretch flex-col justify-start items-center gap-6 flex mb-8">
            <div className="flex-col justify-start items-center gap-2 flex">
              <div className="text-center text-gray-700 text-2xl font-bold font-figtree leading-9">
                {t("register.title")}
              </div>
              {step === 1 ? (
                <div className="justify-start items-center gap-1 inline-flex">
                  <div className="text-gray-500 text-sm font-normal font-figtree leading-tight">
                    {t("register.loginNote")}
                  </div>
                  <div className="justify-start items-center flex">
                    <Link
                      to={"/login"}
                      className="text-blue-600 text-sm font-medium font-figtree leading-tight"
                    >
                      {t("register.loginBtn")}
                    </Link>
                  </div>
                </div>
              ) : (
                <div className="justify-start items-center gap-1 inline-flex">
                  <div className="text-gray-500 text-sm font-normal font-figtree leading-tight">
                    {t("register.loginNote2")}
                  </div>
                </div>
              )}
            </div>
          </div>

          {step === 1 && (
            <React.Fragment>
              <SocialAuth
                className={`w-full ${
                  isCheckingSocialAuth || isSocialLogin
                    ? "opacity-50 pointer-events-none"
                    : ""
                }`}
                providers={["Apple", "Google"]}
                onLoginStartSocialAuthStart={() => {}}
                onResolveSocialAuth={handleResolveSocialAuth}
                onRejectSocialAuth={handleRejectSocialAuth}
                providersIcon={{
                  Apple: (
                    <div className="px-2.5 w-full h-11 p-2 bg-white hover:bg-blue-400/5 rounded-md shadow border border-gray-300 flex flex-row justify-center items-center cursor-pointer relative mb-4">
                      <img
                        src="../../images/apple-black.svg"
                        alt=""
                        className="w-6 h-6 absolute top-[10px] left-[10px]"
                      />
                      <p className="text-gray-700 text-sm font-medium font-figtree leading-tight">
                        {t("register.socialApple")}
                      </p>
                    </div>
                  ),
                  Google: (
                    <div className="px-2.5 w-full h-11 p-2 bg-white hover:bg-blue-400/5  rounded-md shadow border border-gray-300 flex flex-row justify-center items-center cursor-pointer relative">
                      <img
                        src="../../images/google.svg"
                        alt=""
                        className="w-6 h-6 absolute top-[10px] left-[10px]"
                      />
                      <p className="text-gray-700 text-sm font-medium font-figtree leading-tight">
                        {t("register.socialGoogle")}
                      </p>
                    </div>
                  ),
                }}
                redirectUrl={{
                  Apple:
                    (process.env.REACT_APP_HOST_URL as string) + "/register",
                  Google:
                    (process.env.REACT_APP_HOST_URL as string) + "/register",
                }}
              />
              <div className="text-gray-500 text-sm font-extrabold font-figtree leading-tight text-center w-full my-3">
                {getLanguageCode(i18n.language) === "fr" ? "OU" : "OR"}
              </div>
            </React.Fragment>
          )}

          <form
            onSubmit={handleSubmit(onSubmit)}
            className="self-stretch flex-col justify-start items-start gap-6 flex"
          >
            {renderStep()}

            {errors && Object.keys(errors).length > 0 && (
              <div>
                {Object.keys(errors).map((key) => (
                  <p key={key} className="mb-0 text-xs text-red-600">
                    {shownFieldError(
                      t,
                      errors[
                        key as keyof FieldErrors<RegisterForm>
                      ]?.message?.toString()
                    )}
                  </p>
                ))}
              </div>
            )}

            {error?.response?.data?.element &&
              Object.keys(error?.response?.data?.element).length > 0 && (
                <div>
                  {Object.keys(error?.response?.data?.element).map((key) => (
                    <p key={key} className="mb-0 text-xs text-red-600">
                      {error?.response?.data?.element[key]}
                    </p>
                  ))}
                </div>
              )}

            <div className="self-stretch flex-col justify-start items-stretch gap-10 flex">
              {renderStepButton()}

              {step === 1 && partnerReference && (
                <p className="mb-0 text-center text-xs text-gray-400 font-figtree">
                  {t("register.partnerLabel")}: {partnerReference}
                </p>
              )}
            </div>
          </form>
        </div>

        <ModalShell
          show={showConfirmEmailModal}
          onClose={() => {}}
          size="2xl"
          title={t("register.confirmEmailModal.text_1")}
          subtitle={t("register.confirmEmailModal.text_2")}
          showCloseButton={false}
          footer={
            <>
              <CustomButton
                onClick={() =>
                  userData?.user_email &&
                  resendValitionLink(
                    {
                      user_identification: userData.owner_user_identification,
                    },
                    {
                      onSuccess: () => {
                        setShowTimer(true);
                      },
                      onError: (err) => {
                        setShowTimer(false);
                      },
                    }
                  )
                }
                disabled={showTimer}
                isLoading={isResending}
                title={t("register.confirmEmailModal.btn_2") as string}
                comp={
                  showTimer && (
                    <span className="text-muted ms-2">
                      <Timer setShowTimer={setShowTimer} ttl={120} />{" "}
                      {t("register.confirmEmailModal.text_5")}
                    </span>
                  )
                }
              />
              <CustomButton
                variant="primary"
                onClick={handleLogin}
                disabled={isLogin}
                title={t("register.confirmEmailModal.btn_1") as string}
              />
            </>
          }
        >
          <div className="flex justify-center my-6">
            <img
              title="Super User Manager"
              loading="lazy"
              src="https://cdn.1cdn.app/application/UMD/umd_welcome.svg"
              alt="Super User Manager"
              className="img-fluid"
              width={250}
            />
          </div>

          <p
            className="text-sm text-gray-700"
            style={{ wordBreak: "break-word" }}
          >
            {t("register.confirmEmailModal.text_3")}{" "}
            {userData?.user_email && (
              <b className="font-semibold text-gray-700">
                {" "}
                {userData.user_email}{" "}
              </b>
            )}{" "}
            {t("register.confirmEmailModal.text_4")}
          </p>
        </ModalShell>
      </div>
    </React.Fragment>
  );
};

export default Register;
